import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import memberService from '../../services/memberService';
import stripeService from '../../services/stripeService';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import TransactionReport from '../../components/reports/TransactionReport';
import { generatePdfFromElement } from '../../utils/pdfUtils';
import axios from 'axios';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
// Import Material UI components
import {
  Box, Grid, Paper, Typography, Avatar, Chip, Button, Divider,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Alert, Card, CardContent, IconButton, Drawer, List,
  ListItem, ListItemIcon, ListItemText, useTheme, useMediaQuery, Toolbar,
  AppBar, LinearProgress, Container, Badge, Dialog, DialogTitle, 
  DialogContent, DialogContentText, DialogActions, CardMedia, Stack, Collapse,
  TextField, InputAdornment, Menu, Backdrop, MenuItem
} from '@mui/material';
// Import Material UI icons
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  Menu as MenuIcon,
  CreditCard as CreditCardIcon,
  CalendarToday as CalendarIcon,
  Badge as BadgeIcon,
  CardMembership as CardMembershipIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  WarningRounded as WarningRoundedIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Payment as PaymentIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalance as AccountBalanceIcon,
  Print as PrintIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';

const StatCard = ({ title, value, icon, color }) => (
  <Card elevation={2} sx={{ 
    height: '100%', 
    borderLeft: `4px solid ${color}`,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: 3
    }
  }}>
    <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
      <Box display="flex" alignItems="center">
        <Avatar sx={{ 
          bgcolor: `${color}20`, 
          color: color,
          width: { xs: 40, sm: 45 },
          height: { xs: 40, sm: 45 }
        }}>
          {icon}
        </Avatar>
        <Box ml={2} sx={{ width: 'calc(100% - 60px)' }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Typography 
            variant="h5" 
            component="div" 
            sx={{ 
              fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 'medium',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const MetricCard = ({ title, value, icon, description, color = 'primary.main', progress, gradientColors }) => {
  return (
    <Card 
      elevation={3} 
      sx={{ 
        height: '100%',
        background: gradientColors ? 
          `linear-gradient(135deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)` : 
          'white',
        color: gradientColors ? 'white' : 'inherit',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: 6
        }
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography 
            variant="subtitle2" 
            color={gradientColors ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary'}
          >
            {title}
          </Typography>
          <Avatar 
            sx={{ 
              bgcolor: gradientColors ? 'rgba(255, 255, 255, 0.2)' : color, 
              width: 40, 
              height: 40,
              color: gradientColors ? 'white' : 'white'
            }}
          >
            {icon}
          </Avatar>
        </Box>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'medium', 
            mb: 1,
            color: gradientColors ? 'white' : 'inherit'
          }}
        >
          {value}
        </Typography>
        {progress !== undefined && (
          <Box sx={{ width: '100%', mt: 1, mb: 1 }}>
            <LinearProgress 
              variant="determinate" 
              value={progress} 
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: gradientColors ? 'rgba(255, 255, 255, 0.2)' : undefined,
                '& .MuiLinearProgress-bar': {
                  bgcolor: gradientColors ? 'white' : undefined
                }
              }}
            />
          </Box>
        )}
        <Typography 
          variant="body2" 
          color={gradientColors ? 'rgba(255, 255, 255, 0.8)' : 'text.secondary'}
        >
          {description}
        </Typography>
      </CardContent>
    </Card>
  );
};

const MemberDashboardPage = () => {
  const { user, logout } = useAuth();
  const { tenant } = useTenant();
  const [memberData, setMemberData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentAmountDialogOpen, setPaymentAmountDialogOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [stripePromise, setStripePromise] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentError, setPaymentError] = useState('');
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [debts, setDebts] = useState([]);
  const [loadingDebts, setLoadingDebts] = useState(true);
  const [debtError, setDebtError] = useState(null);
  const [debtAlertOpen, setDebtAlertOpen] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(true);
  const [paymentMethodsDialogOpen, setPaymentMethodsDialogOpen] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [alternativePaymentDialogOpen, setAlternativePaymentDialogOpen] = useState(false);
  const [alternativePaymentInfo, setAlternativePaymentInfo] = useState({
    method: '',
    instructions: '',
    accountInfo: ''
  });
  const transactionReportRef = useRef(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const [printReportType, setPrintReportType] = useState('all');
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  
  const dashboardColors = {
    status: {
      active: '#4CAF50',
      pending: '#FF9800',
      inactive: '#F44336'
    },
    membership: '#673AB7',
    payment: '#009688',
    wallet: '#2196F3',
    debt: '#E91E63'
  };

  useEffect(() => {
    const fetchMemberData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          setError('You need to be logged in to access your membership data. Please log in again.');
          setTimeout(() => {
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
          }, 3000);
          setLoading(false);
          return;
        }
        
        localStorage.setItem('token', token);
        
        let tenantId;
        
        if (tenant && (tenant._id || tenant.id)) {
          tenantId = tenant._id || tenant.id;
        } else {
          tenantId = localStorage.getItem('tenantId');
          
          if (!tenantId) {
            const storedTenant = localStorage.getItem('currentTenant');
            if (storedTenant) {
              try {
                const parsedTenant = JSON.parse(storedTenant);
                if (parsedTenant && (parsedTenant._id || parsedTenant.id)) {
                  tenantId = parsedTenant._id || parsedTenant.id;
                }
              } catch (e) {
                console.error('Error parsing tenant from localStorage:', e);
              }
            }
          }
        }
        
        if (!tenantId) {
          setError('Unable to determine your organization. Please try logging in again.');
          setLoading(false);
          return;
        }
        
        localStorage.setItem('tenantId', tenantId);
        
        if (user && user.id) {
          localStorage.setItem('userId', user.id);
        }
        
        const requestConfig = {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-Tenant-ID': tenantId
          }
        };
        
        if (user && user.id) {
          requestConfig.headers['X-User-ID'] = user.id;
        }
        
        try {
          const profileResponse = await memberService.getCurrentMemberProfile(requestConfig);
          handleProfileResponse(profileResponse);
        } catch (firstError) {
          try {
            const profileResponse = await memberService.getCurrentMemberProfile();
            handleProfileResponse(profileResponse);
          } catch (secondError) {
            handleProfileError(secondError);
          }
        }
      } catch (error) {
        handleProfileError(error);
      } finally {
        setLoading(false);
      }
    };

    const handleProfileResponse = (profileResponse) => {
      if (profileResponse && profileResponse.data) {
        const memberData = profileResponse.data.data || profileResponse.data;
        
        if (memberData) {
          setMemberData(memberData);
          
          if (memberData._id) {
            fetchPaymentHistory(memberData._id);
            fetchMemberDebts();
          }
        } else {
          setError('Unable to load your member profile. Please try again later.');
        }
      } else {
        setError('Invalid response format from server.');
      }
    };
    
    const fetchPaymentHistory = async (memberId) => {
      try {
        const paymentResponse = await memberService.getMemberPaymentHistory(memberId);
        if (paymentResponse && paymentResponse.data) {
          const paymentData = paymentResponse.data.data || paymentResponse.data;
          setPayments(paymentData || []);
        }
      } catch (paymentError) {
        console.error('Error fetching payment history:', paymentError);
      }
    };
    
    const handleProfileError = (error) => {
      if (error.response) {
        if (error.response.status === 401) {
          setError('Your session has expired or is invalid. Please log in again.');
          setTimeout(() => {
            logout();
            window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
          }, 3000);
        } else {
          setError('Failed to load your profile data. ' + 
            (error.response.data?.message || error.message || 'Unknown error'));
        }
      } else {
        setError('Failed to load your profile data. Please check your connection and try again.');
      }
    };

    fetchMemberData();
  }, [tenant, logout, user]);
  
  const fetchMemberDebts = async () => {
    try {
      setLoadingDebts(true);
      setDebtError(null);
      
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      try {
        const response = await memberService.getCurrentMemberDebts();
        
        let debtsData = [];
        
        if (response && Array.isArray(response)) {
          debtsData = response;
        } else if (response && response.data && Array.isArray(response.data)) {
          debtsData = response.data;
        } else if (response && response.debts && Array.isArray(response.debts)) {
          debtsData = response.debts;
        }
        
        const activeDebts = debtsData.filter(debt => debt.status === 'active');
        
        setDebts(activeDebts);
      } catch (error) {
        if (memberData && memberData._id) {
          try {
            const fallbackResponse = await memberService.getMemberDebts(memberData._id);
            
            let debtsData = [];
            
            if (fallbackResponse && Array.isArray(fallbackResponse)) {
              debtsData = fallbackResponse;
            } else if (fallbackResponse && fallbackResponse.data && Array.isArray(fallbackResponse.data)) {
              debtsData = fallbackResponse.data;
            } else if (fallbackResponse && fallbackResponse.debts && Array.isArray(fallbackResponse.debts)) {
              debtsData = fallbackResponse.debts;
            }
            
            const activeDebts = debtsData.filter(debt => debt.status === 'active');
            
            setDebts(activeDebts);
          } catch (fallbackError) {
            setDebts([]);
          }
        } else {
          setDebts([]);
        }
      }
    } catch (error) {
      setDebtError('Unable to load outstanding balances');
      setDebts([]);
    } finally {
      setLoadingDebts(false);
    }
  };

  const fetchMemberTransactions = async () => {
    try {
      setLoadingTransactions(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        setLoadingTransactions(false);
        return;
      }
      
      const tenantId = localStorage.getItem('tenantId');
      const userId = localStorage.getItem('userId');
      
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      if (tenantId) {
        config.headers['X-Tenant-ID'] = tenantId;
      }
      
      if (userId) {
        config.headers['X-User-ID'] = userId;
      }
      
      try {
        const response = await memberService.getCurrentMemberTransactions(config);
        handleTransactionsResponse(response);
      } catch (firstError) {
        try {
          const response = await memberService.getCurrentMemberTransactions();
          handleTransactionsResponse(response);
        } catch (secondError) {
          setTransactions([]);
        }
      }
    } catch (error) {
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleTransactionsResponse = (response) => {
    let transactionsData = [];
    
    if (response.data && Array.isArray(response.data)) {
      transactionsData = response.data;
    } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
      transactionsData = response.data.data;
    } else if (response.data && response.data.transactions && Array.isArray(response.data.transactions)) {
      transactionsData = response.data.transactions;
    } else if (Array.isArray(response)) {
      transactionsData = response;
    } else if (response && Array.isArray(response)) {
      transactionsData = response;
    }
    
    setTransactions(transactionsData);
  };

  useEffect(() => {
    if (memberData && memberData._id) {
      fetchMemberTransactions();
    }
  }, [memberData]);

  const getInitials = (firstName, lastName) => {
    const firstInitial = (firstName && typeof firstName === 'string' && firstName.length > 0) ? firstName.charAt(0) : '';
    const lastInitial = (lastName && typeof lastName === 'string' && lastName.length > 0) ? lastName.charAt(0) : '';
    return (firstInitial + lastInitial) || '?';
  };

  const getDaysUntilRenewal = (renewalDate) => {
    if (!renewalDate) return null;
    const now = new Date();
    const renewal = new Date(renewalDate);
    const diffTime = Math.abs(renewal - now);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getMembershipDuration = (joinDate) => {
    if (!joinDate) return 0;
    const now = new Date();
    const join = new Date(joinDate);
    const diffTime = Math.abs(now - join);
    const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30.4));
    return diffMonths;
  };

  const totalPaid = payments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  
  const totalOutstandingDebt = debts.reduce((sum, debt) => sum + (debt.amount || 0), 0);

  const totalTransactionsReceived = transactions && transactions.length > 0 ? 
    transactions.reduce((sum, transaction) => sum + (transaction.amount || 0), 0) : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      alert('Failed to logout. Please try again.');
    }
  };
  
  const openLogoutDialog = () => {
    setLogoutDialogOpen(true);
  };
  
  const closeLogoutDialog = () => {
    setLogoutDialogOpen(false);
  };

  const loadStripeConfig = async () => {
    try {
      const response = await stripeService.getStripeConfig();
      if (response.data && response.data.stripePublishableKey) {
        setStripePromise(loadStripe(response.data.stripePublishableKey));
        return true;
      } else if (response.data && response.data.useSystemGateway) {
        setStripePromise(loadStripe('pk_test_systemPublishableKeyHere'));
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };
  
  const createPaymentIntent = async (amount) => {
    try {
      const response = await stripeService.createPaymentIntent({
        amount: amount * 100,
        currency: 'usd',
        description: 'Payment for membership dues'
      });
      
      if (response.data && response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };
  
  const handleOpenPaymentAmountDialog = () => {
    setPaymentAmount('');
    setPaymentAmountDialogOpen(true);
  };
  
  const handleClosePaymentAmountDialog = () => {
    setPaymentAmountDialogOpen(false);
  };
  
  const handleConfirmPaymentAmount = () => {
    const amount = parseFloat(paymentAmount);
    if (isNaN(amount) || amount <= 0) {
      return;
    }
    
    setPaymentAmountDialogOpen(false);
    setPaymentError('');
    setPaymentSuccess(false);
    setPaymentProcessing(true);
    setPaymentDialogOpen(true);
    
    loadStripeConfig().then(stripeLoaded => {
      if (stripeLoaded) {
        createPaymentIntent(amount).then(paymentIntentCreated => {
          if (!paymentIntentCreated) {
            setPaymentError('Failed to initialize payment. Please try again.');
          }
        });
      } else {
        setPaymentError('Payment system configuration is not available. Please contact support.');
      }
      setPaymentProcessing(false);
    });
  };
  
  const handleOpenPaymentDialog = () => {
    setPaymentMethodsDialogOpen(true);
  };

  const handleClosePaymentMethodsDialog = () => {
    setPaymentMethodsDialogOpen(false);
  };

  const handlePaymentMethodSelect = (method) => {
    setSelectedPaymentMethod(method);
    setPaymentMethodsDialogOpen(false);
    
    if (method === 'stripe') {
      handleOpenPaymentAmountDialog();
    } else {
      handleOpenAlternativePaymentDialog(method);
    }
  };

  const handleOpenAlternativePaymentDialog = (method) => {
    let instructions = '';
    let accountInfo = '';

    switch (method) {
      case 'bank':
        instructions = 'Please transfer the payment amount to our bank account below. Include your membership number in the payment reference.';
        accountInfo = `
Account Name: ${tenant?.name || 'Organization Name'}
Bank: ${tenant?.paymentSettings?.bankName || 'Bank of America'}
Account Number: ${tenant?.paymentSettings?.bankAccountNumber || 'XXXX-XXXX-XXXX-XXXX'}
Routing Number: ${tenant?.paymentSettings?.bankRoutingNumber || 'XXXXXXXXX'}
`;
        break;
      case 'zelle':
        instructions = 'Send your payment to our Zelle account below. Include your membership number in the memo.';
        accountInfo = `
Zelle Email: ${tenant?.paymentSettings?.zelleEmail || 'payments@organization.com'}
Zelle Phone: ${tenant?.paymentSettings?.zellePhone || '(555) 123-4567'}
`;
        break;
      case 'paypal':
        instructions = 'Send your payment to our PayPal account. Include your membership number in the notes.';
        accountInfo = `
PayPal Email: ${tenant?.paymentSettings?.paypalEmail || 'payments@organization.com'}
PayPal Link: ${tenant?.paymentSettings?.paypalLink || 'https://paypal.me/organizationname'}
`;
        break;
      default:
        instructions = 'Please follow the instructions below to complete your payment.';
    }

    setAlternativePaymentInfo({
      method,
      instructions,
      accountInfo
    });

    setAlternativePaymentDialogOpen(true);
  };

  const handleCloseAlternativePaymentDialog = () => {
    setAlternativePaymentDialogOpen(false);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    
    setPaymentAmount('');
    setPaymentError('');
    setPaymentSuccess(false);
    setPaymentProcessing(false);
    setClientSecret('');
  };

  const handlePrintMenuOpen = (event) => {
    setPrintMenuAnchor(event.currentTarget);
  };

  const handlePrintMenuClose = () => {
    setPrintMenuAnchor(null);
  };

  const handleSelectPrintReport = (type) => {
    setPrintReportType(type);
    setPrintDialogOpen(true);
    handlePrintMenuClose();
  };

  const handlePrintReport = async () => {
    try {
      setIsPrinting(true);
      
      if (!transactionReportRef.current) {
        throw new Error('Report component not found');
      }

      const fileName = `${memberData.userId?.lastName || 'Member'}_${memberData.userId?.firstName || ''}_${printReportType}_financial_report`;
      
      await generatePdfFromElement(
        transactionReportRef.current,
        fileName,
        {
          onLoadingStart: () => setIsPrinting(true),
          onLoadingComplete: () => {
            setIsPrinting(false);
            setPrintDialogOpen(false);
          },
          onError: (error) => {
            setIsPrinting(false);
          }
        }
      );
    } catch (error) {
      setIsPrinting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle1" fontWeight="medium">Error</Typography>
          <Typography variant="body2">{error}</Typography>
          <Button 
            onClick={() => window.location.reload()}
            color="error"
            sx={{ mt: 1 }}
            size="small"
          >
            Refresh page
          </Button>
        </Alert>
      </Box>
    );
  }

  if (!memberData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          <Typography variant="subtitle1" fontWeight="medium">No profile found</Typography>
          <Typography variant="body2">
            We couldn't find your membership profile. Please contact your organization administrator.
          </Typography>
        </Alert>
      </Box>
    );
  }

  const userFirstName = memberData.userId?.firstName || '';
  const userLastName = memberData.userId?.lastName || '';
  const userEmail = memberData.userId?.email || '';
  const membershipDuration = getMembershipDuration(memberData?.joinDate);
  const daysUntilRenewal = getDaysUntilRenewal(memberData?.renewalDate);
  const renewalProgress = daysUntilRenewal ? 
    Math.max(0, Math.min(100, 100 - (daysUntilRenewal / 365 * 100))) : 0;

  return (
    <Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          bgcolor: '#f5f7fa',
        }}
      >
        <Paper 
          elevation={0} 
          sx={{ 
            mb: 4, 
            p: 3, 
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
            color: 'white'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium' }}>
                Welcome back, {userFirstName}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.8 }}>
                Welcome to {tenant?.name || 'your organization'}'s member area
              </Typography>
            </Box>
            
            <Button
              onClick={handlePrintMenuOpen}
              variant="outlined"
              startIcon={<PrintIcon />}
              sx={{ 
                color: 'white', 
                borderColor: 'rgba(255, 255, 255, 0.5)',
                '&:hover': {
                  borderColor: 'white',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Financial Report
            </Button>
          </Box>
        </Paper>
        
        {!loadingDebts && debts.length > 0 && debtAlertOpen && (
          <Collapse in={debtAlertOpen}>
            <Paper 
              elevation={3} 
              sx={{ 
                mb: 4, 
                borderRadius: 2,
                overflow: 'hidden',
                border: '1px solid rgba(244, 67, 54, 0.3)',
              }}
            >
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'rgba(244, 67, 54, 0.08)',
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid rgba(244, 67, 54, 0.2)'
                }}
              >
                <WarningRoundedIcon sx={{ color: 'error.main', mr: 1 }} />
                <Typography variant="subtitle1" color="error.main" fontWeight="medium">
                  You have {debts.length} outstanding balance{debts.length !== 1 ? 's' : ''} - Total: {formatCurrency(totalOutstandingDebt)}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  size="small"
                  onClick={() => setDebtAlertOpen(false)}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Box>
              
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: 'rgba(244, 67, 54, 0.03)' }}>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Due Date</TableCell>
                      <TableCell>Description</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {debts.map((debt) => (
                      <TableRow 
                        key={debt._id}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.02)' },
                          '&:hover': { bgcolor: 'rgba(244,67,54,0.05)' }
                        }}
                      >
                        <TableCell>
                          <Typography sx={{ fontWeight: 'medium', textTransform: 'capitalize' }}>
                            {debt.category.replace(/_/g, ' ')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{ fontWeight: 'medium', color: 'error.main' }}>
                            {formatCurrency(debt.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {new Date(debt.dueDate).toLocaleDateString()}
                        </TableCell>
                        <TableCell>{debt.description || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <Box sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.03)', display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleOpenPaymentDialog}
                  startIcon={<PaymentIcon />}
                  sx={{
                    background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)',
                    boxShadow: '0 4px 6px rgba(244, 67, 54, 0.25)',
                  }}
                >
                  Make Payment
                </Button>
              </Box>
            </Paper>
          </Collapse>
        )}
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Membership Status"
              value={memberData.status?.toUpperCase() || 'UNKNOWN'}
              icon={<BadgeIcon />}
              color={
                memberData?.status === 'active' ? dashboardColors.status.active :
                memberData?.status === 'pending' ? dashboardColors.status.pending : 
                dashboardColors.status.inactive
              }
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Membership Duration"
              value={`${membershipDuration} ${membershipDuration === 1 ? 'month' : 'months'}`}
              icon={<CalendarIcon />}
              color={dashboardColors.membership}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Total Payments"
              value={formatCurrency(totalPaid)}
              icon={<CreditCardIcon />}
              color={dashboardColors.payment}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Outstanding Balance"
              value={formatCurrency(totalOutstandingDebt)}
              icon={totalOutstandingDebt > 0 ? <AttachMoneyIcon /> : <CheckCircleIcon />}
              color={totalOutstandingDebt > 0 ? dashboardColors.status.inactive : dashboardColors.status.active}
            />
          </Grid>
        </Grid>
          
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={8}>
            <Card 
              elevation={3} 
              sx={{ 
                height: '100%',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  p: 2, 
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  background: 'linear-gradient(to right, rgba(33, 150, 243, 0.1), rgba(33, 150, 243, 0.02))',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: `${dashboardColors.wallet}20`,
                        color: dashboardColors.wallet,
                        mr: 1.5
                      }}
                    >
                      <AccountBalanceIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
                      Financial Summary
                    </Typography>
                  </Box>
                  
                  <Chip 
                    label={transactions.length > 0 ? 'ACTIVE' : 'NO ACTIVITY'} 
                    size="small"
                    sx={{ 
                      bgcolor: transactions.length > 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(158, 158, 158, 0.1)',
                      color: transactions.length > 0 ? dashboardColors.status.active : 'text.secondary',
                      fontWeight: 'bold',
                      height: 24
                    }}
                  />
                </Box>
                
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: 'rgba(33, 150, 243, 0.15)',  
                        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.15)', 
                        border: '1px solid rgba(33, 150, 243, 0.2)', 
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(33, 150, 243, 0.25)',
                          transform: 'translateY(-2px)'
                        }
                      }}>
                        <Typography variant="subtitle2" color="text.secondary" fontWeight="medium">Funds Received</Typography>
                        <Typography variant="h5" sx={{ mt: 1, mb: 1, fontWeight: 'bold', color: dashboardColors.wallet }}>
                          {formatCurrency(totalTransactionsReceived)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ p: 2, borderRadius: 1, bgcolor: 'rgba(0, 150, 136, 0.08)' }}>
                        <Typography variant="subtitle2" color="text.secondary">Payments Made</Typography>
                        <Typography variant="h5" sx={{ mt: 1, mb: 1, fontWeight: 'medium' }}>
                          {formatCurrency(totalPaid)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {payments.length} payment{payments.length !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Box sx={{ 
                        p: 2, 
                        borderRadius: 1, 
                        bgcolor: totalOutstandingDebt > 0 ? 'rgba(244, 67, 54, 0.08)' : 'rgba(76, 175, 80, 0.08)'
                      }}>
                        <Typography variant="subtitle2" color="text.secondary">Outstanding Balance</Typography>
                        <Typography 
                          variant="h5" 
                          sx={{ 
                            mt: 1, 
                            mb: 1, 
                            fontWeight: 'medium',
                            color: totalOutstandingDebt > 0 ? 'error.main' : dashboardColors.status.active
                          }}
                        >
                          {formatCurrency(totalOutstandingDebt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {totalOutstandingDebt > 0 ? 
                            `${debts.length} unpaid item${debts.length !== 1 ? 's' : ''}` : 
                            'All payments up to date'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                  
                  {transactions.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                        Recent Financial Activity
                      </Typography>
                      
                      <TableContainer sx={{ maxHeight: 200, overflowY: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Date</TableCell>
                              <TableCell>Type</TableCell>
                              <TableCell>Description</TableCell>
                              <TableCell align="right">Amount</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {transactions.slice(0, 3).map((transaction, index) => (
                              <TableRow 
                                key={transaction._id || index}
                                sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}
                              >
                                <TableCell>
                                  {transaction.date ? new Date(transaction.date).toLocaleDateString() : 'N/A'}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label="Received"
                                    size="small"
                                    sx={{ 
                                      bgcolor: 'rgba(76, 175, 80, 0.1)', 
                                      color: dashboardColors.status.active,
                                      fontWeight: 'medium',
                                      fontSize: '0.75rem'
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {transaction.reason?.replace(/_/g, ' ') || 'Transfer'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight="medium" color={dashboardColors.status.active}>
                                    +{formatCurrency(transaction.amount || 0)}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      
                      {transactions.length > 3 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                          <Button 
                            variant="outlined"
                            size="small"
                            color="primary"
                          >
                            View all {transactions.length} transactions
                          </Button>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ borderRadius: 2, height: '100%' }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ 
                  p: 2, 
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  background: 'linear-gradient(to right, rgba(103, 58, 183, 0.1), rgba(103, 58, 183, 0.02))',
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 'medium', color: dashboardColors.membership }}>
                    Quick Actions
                  </Typography>
                </Box>
                
                <Box sx={{ p: 2 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleOpenPaymentDialog}
                    startIcon={<PaymentIcon />}
                    sx={{
                      mb: 2,
                      py: 1.5,
                      background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
                      boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
                    }}
                  >
                    Make a Payment
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handlePrintMenuOpen}
                    startIcon={<PrintIcon />}
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    Financial Report
                  </Button>
                  
                  <Button
                    variant="outlined"
                    fullWidth
                    component={Link}
                    to="/member/profile"
                    startIcon={<EditIcon />}
                    sx={{ mb: 2, py: 1.5 }}
                  >
                    My Profile
                  </Button>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'medium' }}>
                    Membership Details
                  </Typography>
                  
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Membership Number
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {memberData.membershipNumber || 'Not assigned'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Membership Type
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {memberData.membershipType || 'Standard'}
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Join Date
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {memberData.joinDate ? new Date(memberData.joinDate).toLocaleDateString() : 'Unknown'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {payments && payments.length > 0 && (
          <Paper 
            elevation={2} 
            sx={{ 
              mb: 4,
              borderRadius: 2,
              borderLeft: `4px solid ${dashboardColors.payment}`,
              overflow: 'hidden'
            }}
          >
            <Box 
              sx={{ 
                px: 3, 
                py: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                background: `linear-gradient(to right, ${dashboardColors.payment}10 0%, rgba(255,255,255,0) 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar sx={{ bgcolor: `${dashboardColors.payment}20`, color: dashboardColors.payment, mr: 2 }}>
                  <ReceiptIcon />
                </Avatar>
                <Typography variant="h6" fontWeight="medium">Payment History</Typography>
              </Box>
              <Chip 
                label={`${payments.length} ${payments.length === 1 ? 'Payment' : 'Payments'}`} 
                size="small"
                sx={{ 
                  bgcolor: `${dashboardColors.payment}10`, 
                  color: dashboardColors.payment,
                  fontWeight: 'medium'
                }}
              />
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment, index) => (
                    <TableRow 
                      key={index}
                      sx={{ 
                        '&:nth-of-type(odd)': { bgcolor: 'rgba(0,0,0,0.02)' },
                        '&:hover': { bgcolor: `${dashboardColors.payment}05` }
                      }}
                    >
                      <TableCell>
                        {payment.date ? new Date(payment.date).toLocaleDateString() : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight="medium">
                          {formatCurrency(payment.amount || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={payment.method || 'Unknown'}
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(0, 0, 0, 0.05)', 
                            fontWeight: 'medium',
                            fontSize: '0.75rem'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            maxWidth: 300, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap' 
                          }}
                          title={payment.description || payment.notes || 'No description provided'}
                        >
                          {payment.description || payment.notes || 'No description provided'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Box>
      
      <Menu
        anchorEl={printMenuAnchor}
        open={Boolean(printMenuAnchor)}
        onClose={handlePrintMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: { mt: 1, minWidth: 200, borderRadius: 2 }
        }}
      >
        <Typography variant="subtitle2" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
          Select Report Type
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <MenuItem onClick={() => handleSelectPrintReport('all')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: dashboardColors.membership }} />
          Complete Financial Report
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('payments')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: dashboardColors.payment }} />
          Payment Summary
        </MenuItem>
        <MenuItem onClick={() => handleSelectPrintReport('transactions')}>
          <PictureAsPdfIcon fontSize="small" sx={{ mr: 2, color: dashboardColors.wallet }} />
          Transactions Summary
        </MenuItem>
      </Menu>
      
      <Dialog
        open={printDialogOpen}
        onClose={() => !isPrinting && setPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'primary.light',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PrintIcon sx={{ mr: 1 }} />
            Your Financial Report
          </Box>
          {!isPrinting && (
            <IconButton
              onClick={() => setPrintDialogOpen(false)}
              sx={{ color: 'white' }}
            >
              <ArrowBackIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#f5f5f5',
          minHeight: '70vh'
        }}>
          <Box sx={{ my: 2, width: '100%', overflow: 'auto' }}>
            <TransactionReport
              ref={transactionReportRef}
              member={memberData}
              payments={payments}
              transactions={transactions}
              debts={debts}
              reportType={printReportType}
              tenant={tenant}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, bgcolor: 'background.paper' }}>
          <Button
            onClick={() => setPrintDialogOpen(false)}
            disabled={isPrinting}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePrintReport}
            disabled={isPrinting}
            variant="contained"
            startIcon={isPrinting ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
            color="primary"
          >
            {isPrinting ? 'Generating PDF...' : 'Download PDF Report'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={paymentMethodsDialogOpen}
        onClose={handleClosePaymentMethodsDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Select Payment Method
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CreditCardIcon />}
                onClick={() => handlePaymentMethodSelect('stripe')}
                sx={{ py: 1.5 }}
              >
                Credit/Debit Card
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AccountBalanceIcon />}
                onClick={() => handlePaymentMethodSelect('bank')}
                sx={{ py: 1.5 }}
              >
                Bank Transfer
              </Button>
            </Grid>
            {/* Always show Zelle option regardless of tenant settings */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PaymentIcon />}
                onClick={() => handlePaymentMethodSelect('zelle')}
                sx={{ py: 1.5 }}
              >
                Zelle
              </Button>
            </Grid>
            {/* Always show PayPal option regardless of tenant settings */}
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<AttachMoneyIcon />}
                onClick={() => handlePaymentMethodSelect('paypal')}
                sx={{ py: 1.5 }}
              >
                PayPal
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentMethodsDialog} color="primary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={paymentAmountDialogOpen}
        onClose={handleClosePaymentAmountDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Enter Payment Amount
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            label="Amount"
            type="number"
            fullWidth
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentAmountDialog}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmPaymentAmount} 
            variant="contained" 
            color="primary"
            disabled={!paymentAmount || parseFloat(paymentAmount) <= 0}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Make a Payment
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {paymentProcessing ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : paymentError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {paymentError}
            </Alert>
          ) : paymentSuccess ? (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="subtitle1">Payment Successful!</Typography>
              <Typography variant="body2">
                Your payment of {formatCurrency(parseFloat(paymentAmount))} has been processed.
              </Typography>
            </Alert>
          ) : clientSecret && stripePromise ? (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm 
                amount={parseFloat(paymentAmount)} 
                onSuccess={() => setPaymentSuccess(true)} 
                onError={(msg) => setPaymentError(msg)}
              />
            </Elements>
          ) : (
            <Box sx={{ my: 2 }}>
              <Typography>Initializing payment system...</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleClosePaymentDialog}
            disabled={paymentProcessing}
          >
            {paymentSuccess ? 'Close' : 'Cancel'}
          </Button>
          {paymentSuccess && (
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => {
                handleClosePaymentDialog();
                window.location.reload();
              }}
            >
              View Updated Account
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      <Dialog
        open={alternativePaymentDialogOpen}
        onClose={handleCloseAlternativePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          {alternativePaymentInfo.method === 'bank' ? 'Bank Transfer Details' :
           alternativePaymentInfo.method === 'zelle' ? 'Zelle Payment Details' :
           alternativePaymentInfo.method === 'paypal' ? 'PayPal Payment Details' :
           'Payment Details'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ my: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {alternativePaymentInfo.instructions}
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'background.paper' }}>
              <Typography variant="body1" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
                {alternativePaymentInfo.accountInfo}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Your Reference Information:
                </Typography>
                <Typography variant="body2">
                  Membership Number: <b>{memberData.membershipNumber || 'Not assigned'}</b>
                </Typography>
                <Typography variant="body2">
                  Member Name: <b>{userFirstName} {userLastName}</b>
                </Typography>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAlternativePaymentDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isPrinting}
      >
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          p: 4,
          borderRadius: 2
        }}>
          <CircularProgress color="inherit" sx={{ mb: 2 }} />
          <Typography variant="h6">
            Generating PDF...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Please wait, this may take a moment
          </Typography>
        </Box>
      </Backdrop>
    </Box>
  );
};

const PaymentForm = ({ amount, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      onError('Stripe has not been initialized. Please refresh and try again.');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-confirmation`,
        },
        redirect: 'if_required'
      });
      
      if (result.error) {
        onError(result.error.message);
      } else {
        onSuccess();
      }
    } catch (error) {
      onError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !stripe || !elements}
          sx={{
            background: 'linear-gradient(45deg, #3f51b5 30%, #5c6bc0 90%)',
            boxShadow: '0 4px 6px rgba(63, 81, 181, 0.25)',
          }}
        >
          {loading ? (
            <>
              <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} /> Processing...
            </>
          ) : (
            `Pay ${formatCurrency(amount)}`
          )}
        </Button>
      </Box>
    </form>
  );
};

export default MemberDashboardPage;
